/**
 * AudiobookDetailPage - Main audiobook management page with split view
 *
 * Similar to PodcastListPage but tailored for audiobooks.
 * Shows audiobook list on the left and detail view on the right.
 */
package com.castly.admin.ui.audiobooks

import androidx.compose.runtime.*
import com.castly.admin.api.ApiClient
import com.castly.admin.ui.EpisodeImportForm
import com.castly.admin.ui.theme.AppTheme
import com.castly.admin.ui.theme.BorderRadius
import com.castly.admin.ui.theme.LightColors
import com.castly.admin.ui.theme.Spacing
import com.castly.admin.ui.theme.Typography
import com.castly.admin.ui.theme.themeTransition
import com.castly.shared.dto.AudiobookDto
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

@Composable
fun AudiobookDetailPage(
    apiClient: ApiClient,
    audiobookId: String,
    onBack: () -> Unit = {}
) {
    val colors = AppTheme.colors
    var audiobook by remember { mutableStateOf<AudiobookDto?>(null) }
    var isLoading by remember { mutableStateOf(true) }
    var errorMessage by remember { mutableStateOf<String?>(null) }
    var showEpisodeImportDialog by remember { mutableStateOf(false) }
    var activeTab by remember { mutableStateOf(AudiobookDetailTab.DATA) }

    val scope = rememberCoroutineScope()

    // Load audiobook data
    fun loadAudiobook() {
        scope.launch {
            isLoading = true
            errorMessage = null
            try {
                audiobook = apiClient.getAudiobook(audiobookId)
            } catch (e: Exception) {
                errorMessage = e.message ?: "Failed to load audiobook"
            } finally {
                isLoading = false
            }
        }
    }

    LaunchedEffect(audiobookId) {
        loadAudiobook()
    }

    Div({
        style {
            height(100.vh)
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            backgroundColor(Color(colors.surfaceHover))
            themeTransition()
        }
    }) {
        // Top bar with back button
        Div({
            style {
                padding(Spacing.lg)
                backgroundColor(Color(colors.surface))
                property("border-bottom", "1px solid ${colors.border}")
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                gap(Spacing.lg)
                themeTransition()
            }
        }) {
            Button({
                onClick { onBack() }
                style {
                    padding(Spacing.sm, Spacing.lg)
                    fontSize(Typography.base)
                    fontWeight("500")
                    color(Color(colors.text))
                    backgroundColor(Color(colors.surface))
                    border(1.px, LineStyle.Solid, Color(colors.border))
                    borderRadius(BorderRadius.md)
                    cursor("pointer")
                    property("transition", "all 0.2s")
                }
            }) {
                Text("← Back to Audiobooks")
            }

            H1({
                style {
                    fontSize(20.px)
                    fontWeight("600")
                    color(Color(colors.text))
                    margin(0.px)
                }
            }) {
                Text("Audiobook Details")
            }
        }

        // Main content area
        Div({
            style {
                flex("1")
                minHeight(0.px)
                display(DisplayStyle.Flex)
                overflow("hidden")
            }
        }) {
            // Loading state
            if (isLoading) {
                Div({
                    style {
                        flex("1")
                        display(DisplayStyle.Flex)
                        alignItems(AlignItems.Center)
                        justifyContent(JustifyContent.Center)
                        color(Color(colors.textMuted))
                    }
                }) {
                    Text("Loading audiobook...")
                }
            }

            // Error state
            errorMessage?.let { error ->
                Div({
                    style {
                        flex("1")
                        padding(Spacing.xxl)
                    }
                }) {
                    Div({
                        style {
                            backgroundColor(Color(if (colors == LightColors) "#fee2e2" else "#450a0a"))
                            border(1.px, LineStyle.Solid, Color(colors.danger))
                            borderRadius(BorderRadius.md)
                            padding(Spacing.lg)
                            themeTransition()
                        }
                    }) {
                        Div({
                            style {
                                fontSize(Typography.lg)
                                fontWeight("600")
                                color(Color(if (colors == LightColors) "#991b1b" else "#fca5a5"))
                                marginBottom(Spacing.sm)
                            }
                        }) {
                            Text("Error")
                        }
                        Text(error)
                    }
                }
            }

            // Detail view
            audiobook?.let { book ->
                Div({
                    style {
                        flex("1")
                        backgroundColor(Color(colors.surface))
                        overflow("hidden")
                        display(DisplayStyle.Flex)
                        flexDirection(FlexDirection.Column)
                        themeTransition()
                    }
                }) {
                    AudiobookDetailView(
                        audiobook = book,
                        activeTab = activeTab,
                        apiClient = apiClient,
                        onTabChange = { tab -> activeTab = tab },
                        onAudiobookUpdate = { loadAudiobook() },
                        onClose = { onBack() },
                        onShowEpisodeImport = { showEpisodeImportDialog = true }
                    )
                }
            }
        }

        // Episode import dialog modal
        if (showEpisodeImportDialog) {
            Div({
                style {
                    position(Position.Fixed)
                    top(0.px)
                    left(0.px)
                    width(100.percent)
                    height(100.percent)
                    backgroundColor(Color("rgba(0, 0, 0, 0.5)"))
                    display(DisplayStyle.Flex)
                    justifyContent(JustifyContent.Center)
                    alignItems(AlignItems.Center)
                    property("z-index", "1000")
                }
            }) {
                Div({
                    style {
                        maxWidth(600.px)
                        width(90.percent)
                        maxHeight(90.percent)
                        overflowY("auto")
                    }
                }) {
                    EpisodeImportForm(
                        apiClient = apiClient,
                        isbn = audiobookId,
                        onImportComplete = { report ->
                            console.log("Episode import completed", report)
                            // Reload audiobook data to show updated episodes
                            loadAudiobook()
                            // Optionally switch to episodes tab
                            activeTab = AudiobookDetailTab.CHAPTERS
                        },
                        onCancel = {
                            showEpisodeImportDialog = false
                        }
                    )
                }
            }
        }
    }
}
